import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined'
import ErrorOutlined from '@mui/icons-material/ErrorOutlined'
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { alpha } from '@mui/material/styles'
import { setGlobalToastHandler } from '../../utils/globalToast'

const DURACION = 3800
const MAX_TOASTS = 5

const TONOS = {
  success: { Icon: CheckCircleOutlined, paleta: 'success' },
  error: { Icon: ErrorOutlined, paleta: 'error' },
  warning: { Icon: WarningAmberOutlined, paleta: 'warning' },
  info: { Icon: InfoOutlined, paleta: 'info' },
}

const ToastContext = createContext(null)

function ToastItem({ toast, onRemove }) {
  const [out, setOut] = useState(false)
  const cerrado = useRef(false)
  const { Icon, paleta } = TONOS[toast.tone] || TONOS.success

  const cerrar = useCallback(() => {
    if (cerrado.current) return
    cerrado.current = true
    setOut(true)
    setTimeout(() => onRemove(toast.id), 240)
  }, [onRemove, toast.id])

  // Auto-cierre tras la duración
  useEffect(() => {
    const t = setTimeout(cerrar, DURACION)
    return () => clearTimeout(t)
  }, [cerrar])

  return (
    <Box
      sx={(theme) => {
        const tono = theme.palette[paleta].main
        return {
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.custom.line2}`,
          borderRadius: '12px',
          padding: '13px 14px 13px 16px',
          boxShadow: '0 18px 44px rgba(0,0,0,0.4)',
          position: 'relative',
          overflow: 'hidden',
          pointerEvents: 'auto',
          '@keyframes toastIn': {
            from: { transform: 'translateX(30px)', opacity: 0 },
            to: { transform: 'none', opacity: 1 },
          },
          '@keyframes toastOut': {
            to: { transform: 'translateX(30px)', opacity: 0 },
          },
          animation: out
            ? 'toastOut .26s cubic-bezier(.4,0,1,1) forwards'
            : 'toastIn .32s cubic-bezier(.2,.8,.2,1) both',
          // Cinta lateral del color del tono
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: tono,
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            opacity: out ? 0 : 1,
          },
        }
      }}
    >
      <Box
        sx={(theme) => {
          const tono = theme.palette[paleta].main
          return {
            width: 32,
            height: 32,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            flex: '0 0 auto',
            backgroundColor: alpha(tono, 0.13),
            color: tono,
          }
        }}
      >
        <Icon sx={{ fontSize: 17 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, lineHeight: 1.4 }}>
        {toast.message}
      </Box>
      <IconButton
        size="small"
        onClick={cerrar}
        aria-label="Cerrar"
        sx={(theme) => ({
          flex: '0 0 auto',
          padding: '2px',
          borderRadius: '6px',
          color: theme.palette.custom.inkDim,
          '&:hover': { color: 'text.primary', backgroundColor: theme.palette.custom.bg2 },
        })}
      >
        <CloseOutlined sx={{ fontSize: 15 }} />
      </IconButton>
      {/* Barra de progreso */}
      <Box
        sx={(theme) => ({
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: 2,
          background: theme.palette[paleta].main,
          opacity: 0.5,
          '@keyframes toastProg': {
            from: { width: '100%' },
            to: { width: '0%' },
          },
          animation: `toastProg ${DURACION}ms linear forwards`,
          '@media (prefers-reduced-motion: reduce)': { display: 'none' },
        })}
      />
    </Box>
  )
}

// Provider de toasts propio (sin notistack): stack abajo-derecha, máx 5 visibles
export function ToastProvider({ children }) {
  const [items, setItems] = useState([])
  const seq = useRef(0)

  const push = useCallback((tone, message) => {
    seq.current += 1
    setItems((prev) => {
      const next = [...prev, { id: seq.current, tone, message }]
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next
    })
  }, [])

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const api = useMemo(
    () => ({
      success: (msg) => push('success', msg),
      error: (msg) => push('error', msg),
      warning: (msg) => push('warning', msg),
      info: (msg) => push('info', msg),
    }),
    [push]
  )

  // Permitir emitir toasts desde fuera de React (interceptor de axios → errores de red/5xx)
  useEffect(() => {
    setGlobalToastHandler(api)
    return () => setGlobalToastHandler(null)
  }, [api])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Box
        role="status"
        aria-live="polite"
        sx={(theme) => ({
          position: 'fixed',
          bottom: 'calc(22px + env(safe-area-inset-bottom))',
          right: 22,
          zIndex: theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: '11px',
          width: 360,
          maxWidth: 'calc(100vw - 32px)',
          pointerEvents: 'none',
          '@media (max-width: 860px)': {
            bottom: 'calc(80px + env(safe-area-inset-bottom))',
            right: 12,
            left: 12,
            width: 'auto',
            maxWidth: 'none',
          },
        })}
      >
        {items.map((t) => (
          <ToastItem key={t.id} toast={t} onRemove={remove} />
        ))}
      </Box>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast debe usarse dentro de <ToastProvider>')
  return ctx
}

export default ToastProvider
